import { useCallback, useEffect, useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { Link } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import type { Anwesenheit, Benutzer, Rolle } from '../../../gemeinsam/lib/typen';
import {
  Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner,
} from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';

const LEERES_FORMULAR = { username: '', name: '', email: '', password: '', role: 'employee' as Rolle };
const kuerzel = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

export default function Team() {
  const { t } = useSprache();
  const { benutzer: ich, flags, istAdmin } = useAuth();
  const [liste, setListe] = useState<Benutzer[] | null>(null);
  const [anwesend, setAnwesend] = useState<Record<string, Anwesenheit>>({});
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const [formularOffen, setFormularOffen] = useState(false);
  const [formular, setFormular] = useState(LEERES_FORMULAR);
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({});
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [erfolg, setErfolg] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const [benutzer, praesenz] = await Promise.all([
        api<Benutzer[]>('/users'),
        api<Anwesenheit[]>('/presence/users').catch(() => [] as Anwesenheit[]),
      ]);
      setListe(benutzer);
      setAnwesend(Object.fromEntries(praesenz.map(p => [p.id, p])));
      setLadeFehler(null);
    } catch (e) {
      setLadeFehler(e instanceof ApiFehler ? e.message : 'Team konnte nicht geladen werden.');
      setListe([]);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  const grenzeErreicht =
    !!flags && flags.maxUsers >= 0 && !!liste && liste.length >= flags.maxUsers;

  function pruefen() {
    const f: Record<string, string> = {};
    if (!formular.username.trim()) f.username = 'Benutzername ist erforderlich';
    else if (formular.username.trim().length < 3) f.username = 'Mindestens 3 Zeichen';
    if (!formular.name.trim()) f.name = 'Name ist erforderlich';
    if (!formular.password) f.password = 'Passwort ist erforderlich';
    else if (formular.password.length < 8) f.password = 'Mindestens 8 Zeichen';
    setFeldFehler(f);
    return Object.keys(f).length === 0;
  }

  async function anlegen(e: React.FormEvent) {
    e.preventDefault();
    setSendeFehler(null);
    setErfolg(null);
    if (!pruefen()) return;

    setSendet(true);
    try {
      const neu = await api<Benutzer>('/users', {
        methode: 'POST',
        daten: {
          username: formular.username.trim(),
          name: formular.name.trim(),
          email: formular.email.trim() || undefined,
          password: formular.password,
          role: formular.role,
        },
      });
      setListe(l => [...(l ?? []), neu]);
      setFormular(LEERES_FORMULAR);
      setFormularOffen(false);
      setErfolg(`${neu.name} wurde angelegt und kann sich sofort anmelden.`);
    } catch (e) {
      // Das Benutzerlimit des Tarifs kommt als 402 — eigener, klarer Hinweis.
      if (e instanceof ApiFehler && e.code === 'USER_LIMIT_REACHED') {
        setSendeFehler(`${e.message} (${e.daten?.current} von ${e.daten?.maxUsers} belegt)`);
      } else {
        setSendeFehler(e instanceof ApiFehler ? e.message : 'Anlegen fehlgeschlagen.');
      }
    } finally {
      setSendet(false);
    }
  }

  async function entfernen(b: Benutzer) {
    if (!confirm(`${b.name} wirklich aus dem Team entfernen? Das lässt sich nicht rückgängig machen.`)) return;
    try {
      await api(`/users/${b.id}`, { methode: 'DELETE' });
      setListe(l => (l ?? []).filter(x => x.id !== b.id));
      setErfolg(`${b.name} wurde entfernt.`);
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Entfernen fehlgeschlagen.');
    }
  }

  if (liste === null) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('team.titel')}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {liste.length} {liste.length === 1 ? 'Mitglied' : 'Mitglieder'}
          {flags && flags.maxUsers >= 0 && ` von ${flags.maxUsers} im Tarif`}
        </p>
      </div>

      {ladeFehler && <Hinweis art="fehler">{ladeFehler}</Hinweis>}
      <Erfolgsmeldung text={erfolg} />

      {istAdmin && grenzeErreicht && !formularOffen && (
        <Hinweis art="warnung" titel={t('team.limitErreicht')}>
          Ihr Tarif erlaubt {flags?.maxUsers} Benutzer. Für weitere Mitarbeiter wechseln Sie den Tarif.
        </Hinweis>
      )}

      <Karte>
        <KartenKopf
          titel={t('team.mitglieder')}
          hinweis={istAdmin ? 'Sie können Mitarbeiter anlegen und entfernen.' : 'Nur Administratoren können das Team ändern.'}
          aktion={istAdmin && !formularOffen && (
            <Knopf variante="primaer" onClick={() => { setFormularOffen(true); setErfolg(null); }} disabled={grenzeErreicht}>
              Mitarbeiter hinzufügen
            </Knopf>
          )}
        />

        {formularOffen && (
          <form onSubmit={anlegen} className="space-y-4 border-b border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950/50">
            {sendeFehler && <Hinweis art="fehler">{sendeFehler}</Hinweis>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Feld
                label={t('team.benutzername')}
                value={formular.username}
                onChange={e => setFormular(f => ({ ...f, username: e.target.value }))}
                fehler={feldFehler.username}
                autoComplete="off"
                autoFocus
              />
              <Feld
                label={t('feld.name')}
                value={formular.name}
                onChange={e => setFormular(f => ({ ...f, name: e.target.value }))}
                fehler={feldFehler.name}
                autoComplete="off"
              />
              <Feld
                label={t('team.emailOptional')}
                type="email"
                value={formular.email}
                onChange={e => setFormular(f => ({ ...f, email: e.target.value }))}
                autoComplete="off"
              />
              <Feld
                label={t('anmelden.passwort')}
                type="password"
                value={formular.password}
                onChange={e => setFormular(f => ({ ...f, password: e.target.value }))}
                fehler={feldFehler.password}
                hinweis={t('team.passwortHinweis')}
                autoComplete="new-password"
              />
              <Auswahl
                label={t('team.rolle')}
                value={formular.role}
                onChange={e => setFormular(f => ({ ...f, role: e.target.value as Rolle }))}
              >
                <option value="employee">{t('allg.rolle.mitarbeiter')}</option>
                <option value="admin">{t('allg.rolle.admin')}</option>
              </Auswahl>
            </div>

            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet}>{t('aktion.anlegen')}</Knopf>
              <Knopf type="button" onClick={() => { setFormularOffen(false); setFeldFehler({}); setSendeFehler(null); }}>
                Abbrechen
              </Knopf>
            </div>
          </form>
        )}

        {liste.length === 0 ? (
          <LeerZustand titel={t('team.leer')} text={t('team.leerText')} />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(b => {
              const online = anwesend[b.id]?.online;
              return (
                <li key={b.id} className="group flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <Link to={`/team/${b.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-marke-100 text-sm font-semibold text-marke-800 dark:bg-marke-950 dark:text-marke-200">
                      {kuerzel(b.name)}
                      <span
                        className={`absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full ring-2 ring-white dark:ring-slate-900 ${online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                        title={t(online ? 'team.online' : 'team.offline')}
                        aria-label={t(online ? 'team.online' : 'team.offline')}
                      />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900 group-hover:text-marke-700 dark:text-slate-100 dark:group-hover:text-marke-300">
                        {b.name}
                        {b.id === ich?.id && <span className="ms-2 text-xs font-normal text-slate-500 dark:text-slate-400">({t('team.sie')})</span>}
                      </p>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                        {[[b.profil?.position, b.profil?.abteilung].filter(Boolean).join(' · ') || b.username, b.profil?.telefon || b.profil?.mobil || b.email]
                          .filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </Link>

                  {b.isOwner
                    ? <Abzeichen art="blau">{t('allg.rolle.inhaber')}</Abzeichen>
                    : b.role === 'admin'
                      ? <Abzeichen art="gruen">{t('allg.rolle.admin')}</Abzeichen>
                      : <Abzeichen>{t('allg.rolle.mitarbeiter')}</Abzeichen>}

                  {b.status === 'frozen' && <Abzeichen art="rot">{t('team.gesperrt')}</Abzeichen>}

                  {istAdmin && !b.isOwner && b.id !== ich?.id && (
                    <Knopf variante="still" onClick={() => entfernen(b)} aria-label={`${b.name} entfernen`}>
                      Entfernen
                    </Knopf>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Karte>
    </div>
  );
}
